import type { WidgetConfig, Message } from '../types'
import { API_CLIENT } from '../api-client'

// Types pour les tool actions
interface ToolAction {
  type: 'collect_contact_form' | 'create_lead' | 'show_calendly' | 'end_conversation'
  data: Record<string, unknown>
  toolCallId: string
}

// État du formulaire
interface FormData {
  firstName: string
  lastName: string
  email: string
  phone: string
  company?: string
}

// Contexte de la conversation pour le formulaire
interface ConversationContext {
  clientType?: 'b2c' | 'b2b'
  category?: string
  description?: string
  wantsAppointment?: boolean
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

export class ChatWindow {
  private container: HTMLDivElement
  private window: HTMLDivElement
  private messagesContainer: HTMLDivElement
  private inputField: HTMLTextAreaElement
  private sendButton: HTMLButtonElement
  private isOpen = false
  private messages: Message[] = []
  private sessionId: string | null = null
  private config: WidgetConfig
  private isTyping = false
  private conversationContext: ConversationContext = {}
  private pendingToolCallId: string | null = null
  private currentFormData: FormData | null = null

  constructor(config: WidgetConfig) {
    this.config = config

    // Charte Consullex 2026 : une seule version, claire (plus de thème sombre ni de sélecteur)
    this.container = this.createContainer()
    this.window = this.createWindow()
    this.messagesContainer = this.createMessagesContainer()
    const inputArea = this.createInputArea()
    this.inputField = inputArea.input
    this.sendButton = inputArea.button

    this.window.appendChild(this.messagesContainer)
    this.window.appendChild(inputArea.container)
    
    if (config.showPoweredBy !== false) {
      this.window.appendChild(this.createPoweredBy())
    }

    this.container.appendChild(this.createButton())
    this.container.appendChild(this.window)

    // Auto-initialiser le chat
    this.initializeChat()
  }

  private createContainer(): HTMLDivElement {
    const container = document.createElement('div')
    container.className = `consullex-chatbot ${this.config.position}`
    return container
  }

  private createButton(): HTMLButtonElement {
    const button = document.createElement('button')
    button.className = 'consullex-chat-button'
    // Logo officiel Consullex (charte 2026) : CONSUL en bleu, LEX en noir
    button.innerHTML = `<span class="consullex-logo" aria-hidden="true"><span class="consullex-logo-consul">CONSUL</span><span class="consullex-logo-lex">LEX</span></span>`
    button.setAttribute('aria-label', 'Ouvrir le chat Consullex')
    button.addEventListener('click', () => this.toggle())
    return button
  }

  private createWindow(): HTMLDivElement {
    const window = document.createElement('div')
    window.className = 'consullex-chat-window'

    const header = document.createElement('div')
    header.className = 'consullex-chat-header'
    header.innerHTML = `
      <div class="consullex-chat-header-content">
        <div class="consullex-chat-header-text">
          <h3 class="consullex-logo" aria-label="Consullex"><span class="consullex-logo-consul">CONSUL</span><span class="consullex-logo-lex">LEX</span></h3>
          <p>Votre assistant juridique</p>
        </div>
      </div>
      <button class="consullex-chat-close" aria-label="Fermer le chat">
        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
          <line x1="18" y1="6" x2="6" y2="18"/>
          <line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>
    `

    header.querySelector('.consullex-chat-close')?.addEventListener('click', () => this.close())
    window.appendChild(header)

    return window
  }

  private createMessagesContainer(): HTMLDivElement {
    const container = document.createElement('div')
    container.className = 'consullex-chat-messages'
    return container
  }

  private createInputArea(): {
    container: HTMLDivElement
    input: HTMLTextAreaElement
    button: HTMLButtonElement
  } {
    const container = document.createElement('div')
    container.className = 'consullex-chat-input'

    const input = document.createElement('textarea')
    input.className = 'consullex-chat-input-field'
    input.placeholder = 'Écrivez votre message...'
    input.rows = 1

    input.addEventListener('input', () => {
      input.style.height = 'auto'
      input.style.height = Math.min(input.scrollHeight, 100) + 'px'
    })

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        this.sendMessage()
      }
    })

    const button = document.createElement('button')
    button.className = 'consullex-chat-send'
    button.setAttribute('aria-label', 'Envoyer le message')
    button.innerHTML = `
      <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
        <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
      </svg>
    `
    button.addEventListener('click', () => this.sendMessage())

    container.appendChild(input)
    container.appendChild(button)

    return { container, input, button }
  }

  private createPoweredBy(): HTMLDivElement {
    const wrapper = document.createElement('div')
    wrapper.className = 'consullex-footer-wrapper'

    // Texte powered by
    const powered = document.createElement('div')
    powered.className = 'consullex-powered-by'
    powered.innerHTML = `Propulsé par <a href="https://www.amplify-belgium.com" target="_blank" rel="noopener noreferrer">Amplify</a>`
    
    wrapper.appendChild(powered)
    
    return wrapper
  }

  private async initializeChat() {
    try {
      const response = await API_CLIENT.initChat()
      this.sessionId = response.sessionId
      this.addBotMessage(response.message, response.suggestedReplies)
    } catch (error) {
      console.error('Failed to initialize chat:', error)
      this.addBotMessage(
        `Le chatbot n'est pas encore prêt côté serveur.\n\n${error instanceof Error ? error.message : ''}`.trim()
      )
    }
  }

  private async sendMessage(overrideMessage?: string) {
    const message = overrideMessage || this.inputField.value.trim()
    if (!message || this.isTyping) return

    // Ajouter le message de l'utilisateur
    this.addUserMessage(message)
    this.inputField.value = ''
    this.inputField.style.height = 'auto'

    // === INTERCEPTER LES RÉPONSES CALENDLY (B2B) ===
    const lowerMsg = message.toLowerCase()
    if (this.currentFormData && this.conversationContext.clientType === 'b2b') {
      // L'utilisateur répond à la question Calendly
      if (lowerMsg.includes('oui') || lowerMsg.includes('rendez-vous')) {
        this.conversationContext.wantsAppointment = true
        // Sauvegarder la réponse de l'utilisateur
        await this.saveMessagesToServer([{ role: 'user', content: message }])
        await this.showCalendlyForB2B()
        return
      } else if (lowerMsg.includes('non') || lowerMsg.includes('recontacter')) {
        this.conversationContext.wantsAppointment = false
        // Sauvegarder la réponse de l'utilisateur
        await this.saveMessagesToServer([{ role: 'user', content: message }])
        await this.createLeadFromForm(this.currentFormData)
        return
      }
    }

    // Désactiver l'input
    this.setInputEnabled(false)
    this.showTyping()

    try {
      const response = await API_CLIENT.sendMessage(this.sessionId, message)

      this.sessionId = response.sessionId

      // Cacher typing et ajouter la réponse
      this.hideTyping()
      
      if (response.message) {
        this.addBotMessage(response.message, response.suggestedReplies)
      }

      // Gérer les actions (tool calls)
      if (response.actions) {
        for (const action of response.actions) {
          await this.handleToolAction(action)
        }
      }
    } catch (error) {
      console.error('Failed to send message:', error)
      this.hideTyping()
      this.addBotMessage(
        `Erreur côté serveur.\n\n${error instanceof Error ? error.message : ''}`.trim()
      )
    } finally {
      this.setInputEnabled(true)
    }
  }

  /**
   * Gère les tool actions reçues de GPT
   */
  private async handleToolAction(action: ToolAction) {
    console.log('Handling tool action:', action.type, action.data)

    switch (action.type) {
      case 'collect_contact_form':
        // Stocker le contexte pour le formulaire
        this.conversationContext = {
          clientType: action.data.client_type as 'b2c' | 'b2b',
          category: action.data.category as string,
          description: action.data.description as string,
          wantsAppointment: action.data.wants_appointment as boolean | undefined
        }
        this.pendingToolCallId = action.toolCallId
        this.showContactForm()
        break

      case 'create_lead':
        await this.createLead(action.data)
        break

      case 'show_calendly':
        await this.showCalendly(action.data)
        break

      case 'end_conversation':
        this.endConversation(action.data)
        break
    }
  }

  /**
   * Affiche le formulaire de contact
   */
  private showContactForm() {
    const isB2B = this.conversationContext.clientType === 'b2b'
    
    const formContainer = document.createElement('div')
    formContainer.className = 'consullex-contact-form'
    formContainer.innerHTML = `
      <div class="consullex-form-header">
        <h4>Vos coordonnées</h4>
        <p>Pour que notre équipe puisse vous recontacter</p>
      </div>
      <form class="consullex-form">
        <div class="consullex-form-row">
          <div class="consullex-form-group">
            <label for="firstName">Prénom *</label>
            <input type="text" id="firstName" name="firstName" required placeholder="Jean" />
          </div>
          <div class="consullex-form-group">
            <label for="lastName">Nom *</label>
            <input type="text" id="lastName" name="lastName" required placeholder="Dupont" />
          </div>
        </div>
        <div class="consullex-form-group">
          <label for="email">Email *</label>
          <input type="email" id="email" name="email" required placeholder="jean.dupont@email.com" />
        </div>
        <div class="consullex-form-group">
          <label for="phone">Téléphone *</label>
          <input type="tel" id="phone" name="phone" required placeholder="0470 12 34 56" />
        </div>
        ${isB2B ? `
          <div class="consullex-form-group">
            <label for="company">Entreprise *</label>
            <input type="text" id="company" name="company" required placeholder="Nom de votre entreprise" />
          </div>
        ` : ''}
        <button type="submit" class="consullex-form-submit">
          Valider mes coordonnées
        </button>
      </form>
    `

    // Gérer la soumission du formulaire
    const form = formContainer.querySelector('form')!
    form.addEventListener('submit', async (e) => {
      e.preventDefault()
      
      const formData: FormData = {
        firstName: (form.querySelector('#firstName') as HTMLInputElement).value.trim(),
        lastName: (form.querySelector('#lastName') as HTMLInputElement).value.trim(),
        email: (form.querySelector('#email') as HTMLInputElement).value.trim(),
        phone: (form.querySelector('#phone') as HTMLInputElement).value.trim(),
      }

      if (isB2B) {
        formData.company = (form.querySelector('#company') as HTMLInputElement).value.trim()
      }

      // Valider les champs
      if (!this.validateFormData(formData, isB2B)) {
        return
      }

      // Désactiver le formulaire
      const submitBtn = form.querySelector('button[type="submit"]') as HTMLButtonElement
      submitBtn.disabled = true
      submitBtn.textContent = 'Envoi en cours...'

      // Envoyer les données au backend
      await this.submitContactForm(formData, formContainer)
    })

    this.messagesContainer.appendChild(formContainer)
    this.scrollToBottom()
    
    // Focus sur le premier champ
    setTimeout(() => {
      (formContainer.querySelector('#firstName') as HTMLInputElement)?.focus()
    }, 100)
  }

  /**
   * Valide les données du formulaire
   */
  private validateFormData(data: FormData, isB2B: boolean): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    const phoneRegex = /^(\+32|0)[0-9\s]{8,12}$/

    if (!data.firstName || data.firstName.length < 2) {
      this.showFormError('Veuillez entrer un prénom valide')
      return false
    }

    if (!data.lastName || data.lastName.length < 2) {
      this.showFormError('Veuillez entrer un nom valide')
      return false
    }

    if (!emailRegex.test(data.email)) {
      this.showFormError('Veuillez entrer une adresse email valide')
      return false
    }

    const cleanPhone = data.phone.replace(/\s/g, '')
    if (!phoneRegex.test(cleanPhone)) {
      this.showFormError('Veuillez entrer un numéro de téléphone belge valide (ex: 0470 12 34 56)')
      return false
    }

    if (isB2B && (!data.company || data.company.length < 2)) {
      this.showFormError("Veuillez entrer le nom de votre entreprise")
      return false
    }

    return true
  }

  /**
   * Affiche une erreur dans le formulaire
   */
  private showFormError(message: string) {
    // Supprimer l'ancienne erreur
    const oldError = this.messagesContainer.querySelector('.consullex-form-error')
    if (oldError) oldError.remove()

    const error = document.createElement('div')
    error.className = 'consullex-form-error'
    error.textContent = message
    
    const form = this.messagesContainer.querySelector('.consullex-contact-form')
    if (form) {
      form.insertBefore(error, form.querySelector('.consullex-form'))
      setTimeout(() => error.remove(), 5000)
    }
  }

  /**
   * Soumet le formulaire de contact au backend
   * Flow selon schéma client:
   * - B2C: Créer lead → Confirmation
   * - B2B: Proposer RDV Calendly → Créer lead → Confirmation
   */
  private async submitContactForm(formData: FormData, formContainer: HTMLElement) {
    try {
      // Stocker les données du formulaire
      this.currentFormData = formData

      // Supprimer le formulaire
      formContainer.remove()

      // Pour les B2B, proposer Calendly AVANT de créer le lead
      if (this.conversationContext.clientType === 'b2b') {
        this.askForAppointment(formData)
      } else {
        // Pour les B2C, créer le lead directement
        await this.createLeadFromForm(formData)
      }

    } catch (error) {
      console.error('Failed to submit form:', error)
      this.showFormError("Une erreur est survenue. Veuillez réessayer.")
      
      const submitBtn = formContainer.querySelector('button[type="submit"]') as HTMLButtonElement
      if (submitBtn) {
        submitBtn.disabled = false
        submitBtn.textContent = 'Valider mes coordonnées'
      }
    }
  }

  /**
   * Demande au client B2B s'il veut prendre RDV (selon schéma client)
   */
  private async askForAppointment(formData: FormData) {
    const message = "Souhaitez-vous prendre rendez-vous avec notre équipe ?"
    this.addBotMessage(message, ['Oui, prendre rendez-vous', 'Non merci, me recontacter'])
    
    // Sauvegarder ce message dans la conversation côté serveur
    await this.saveMessagesToServer([{ role: 'assistant', content: message }])
    
    // Stocker les données
    this.currentFormData = formData
    this.conversationContext.wantsAppointment = undefined
  }

  /**
   * Sauvegarde des messages dans la DB pour synchronisation
   */
  private async saveMessagesToServer(messages: Array<{ role: 'user' | 'assistant'; content: string }>) {
    if (!this.sessionId) return
    
    try {
      await API_CLIENT.saveMessages(this.sessionId, messages)
      console.log('[Sync] Messages saved to server:', messages.length)
    } catch (error) {
      console.error('[Sync] Failed to save messages:', error)
    }
  }

  /**
   * Crée un lead et affiche la confirmation finale
   * Selon schéma: confirme téléphone, email (et date RDV si B2B avec RDV)
   */
  private async createLeadFromForm(formData: FormData, appointmentDate?: string) {
    try {
      // IMPORTANT: Les noms de champs doivent correspondre à ce que l'API attend (camelCase)
      const leadData = {
        type: this.conversationContext.clientType || 'b2c',
        category: this.conversationContext.category || 'Autre',
        description: this.conversationContext.description || '',
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone.replace(/\s/g, ''),
        company: formData.company || undefined,
        wantsContact: true
      }

      console.log('[Lead] Creating with data:', leadData)
      const result = await API_CLIENT.createLead(this.sessionId!, leadData)
      
      // Message de confirmation selon schéma client
      let confirmationMessage = `Merci ${formData.firstName}.\n\n` +
        `Votre demande a été enregistrée (réf: ${result.referenceNumber || 'CLX-XXXXXX'}).\n\n` +
        `Nous vous recontacterons :\n` +
        `${formData.email}\n` +
        `${formData.phone}`

      if (appointmentDate) {
        confirmationMessage += `\nRendez-vous : ${appointmentDate}`
      } else {
        confirmationMessage += `\n\nNotre équipe vous recontactera sous 48h.`
      }

      confirmationMessage += `\n\nMerci pour votre confiance.`
      
      this.addBotMessage(confirmationMessage)
      
      // Sauvegarder le message de confirmation dans la DB
      await this.saveMessagesToServer([{ role: 'assistant', content: confirmationMessage }])
      
      // Réinitialiser les données temporaires
      this.currentFormData = null
      this.setInputEnabled(false)
    } catch (error) {
      console.error('Failed to create lead:', error)
      const errorMessage = "Désolé, une erreur est survenue. Notre équipe a été notifiée et vous recontactera."
      this.addBotMessage(errorMessage)
      await this.saveMessagesToServer([{ role: 'assistant', content: errorMessage }])
    }
  }

  /**
   * Charge le SDK Calendly si pas déjà chargé
   */
  private loadCalendlySDK(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Vérifier si déjà chargé
      if ((window as any).Calendly) {
        resolve()
        return
      }

      const script = document.createElement('script')
      script.src = 'https://assets.calendly.com/assets/external/widget.js'
      script.async = true
      script.onload = () => {
        console.log('[Calendly] SDK loaded')
        resolve()
      }
      script.onerror = () => reject(new Error('Failed to load Calendly SDK'))
      document.head.appendChild(script)

      // Ajouter le CSS Calendly aussi
      const link = document.createElement('link')
      link.href = 'https://assets.calendly.com/assets/external/widget.css'
      link.rel = 'stylesheet'
      document.head.appendChild(link)
    })
  }

  /**
   * Helper pour vérifier si un event vient de Calendly
   */
  private isCalendlyEvent(e: MessageEvent): boolean {
    return e.origin === 'https://calendly.com' && 
           e.data.event && 
           e.data.event.indexOf('calendly.') === 0
  }

  /**
   * Affiche Calendly pour les B2B qui veulent un RDV
   * Utilise le SDK officiel Calendly pour une détection automatique des RDV
   */
  private async showCalendlyForB2B() {
    if (!this.currentFormData) return

    const formData = this.currentFormData

    try {
      // Afficher un message de chargement
      this.addBotMessage("Chargement du calendrier...")

      // Charger le SDK Calendly
      await this.loadCalendlySDK()

      const { embedUrl } = await API_CLIENT.getCalendlyUrl(
        formData.firstName,
        formData.lastName,
        formData.email
      )

      console.log('[Calendly] URL:', embedUrl)

      // Supprimer le message de chargement (bon sélecteur: .consullex-message.bot)
      const allBotMessages = this.messagesContainer.querySelectorAll('.consullex-message.bot')
      const lastBotMsg = allBotMessages[allBotMessages.length - 1]
      if (lastBotMsg && lastBotMsg.textContent?.includes('Chargement')) {
        lastBotMsg.remove()
      }

      this.addBotMessage("Choisissez un créneau qui vous convient :")

      // Créer le conteneur pour le widget Calendly
      const calendlyContainer = document.createElement('div')
      calendlyContainer.className = 'consullex-calendly-wrapper'
      calendlyContainer.id = 'calendly-b2b'
      calendlyContainer.style.cssText = 'min-height: 630px; background: white; margin: 12px 0; border-radius: 8px; overflow: hidden;'

      this.messagesContainer.appendChild(calendlyContainer)
      this.scrollToBottom()

      // Initialiser le widget Calendly avec le SDK officiel
      const Calendly = (window as any).Calendly
      Calendly.initInlineWidget({
        url: embedUrl,
        parentElement: calendlyContainer,
        prefill: {
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          customAnswers: {
            a1: formData.phone || ''
          }
        }
      })

      // Écouter les événements Calendly avec le handler officiel
      this.setupCalendlyEventListener(formData)

    } catch (error) {
      console.error('Failed to load Calendly:', error)
      // Supprimer le message de chargement s'il existe
      const allBotMessages = this.messagesContainer.querySelectorAll('.consullex-message.bot')
      const lastBotMsg = allBotMessages[allBotMessages.length - 1]
      if (lastBotMsg && lastBotMsg.textContent?.includes('Chargement')) {
        lastBotMsg.remove()
      }
      this.addBotMessage(
        "Désolé, impossible de charger le calendrier. Un juriste vous contactera directement."
      )
      await this.createLeadFromForm(formData)
    }
  }

  /**
   * Configure l'écoute des événements Calendly (SDK officiel)
   * Détecte automatiquement quand un RDV est pris
   */
  private setupCalendlyEventListener(formData: FormData) {
    const handleCalendlyEvent = async (e: MessageEvent) => {
      // Vérifier que c'est bien un event Calendly
      if (!this.isCalendlyEvent(e)) return

      console.log('[Calendly] Event received:', e.data.event)

      if (e.data.event === 'calendly.event_scheduled') {
        // RDV CONFIRMÉ ! 
        window.removeEventListener('message', handleCalendlyEvent)
        
        // Logger le payload complet pour debug
        console.log('[Calendly] Appointment scheduled! Full payload:', JSON.stringify(e.data.payload, null, 2))

        // Extraire les infos du RDV
        const payload = e.data.payload || {}
        const invitee = payload.invitee || {}
        const event = payload.event || {}
        
        // Calendly embed envoie start_time dans event.start_time OU dans payload directement
        const startTime = event.start_time || payload.start_time || payload.scheduled_event?.start_time
        
        // Formater la date du RDV
        let appointmentDate = 'Confirmé'
        let scheduledAt = new Date().toISOString()
        
        if (startTime) {
          scheduledAt = startTime
          appointmentDate = new Date(startTime).toLocaleDateString('fr-BE', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            hour: '2-digit',
            minute: '2-digit'
          })
          console.log('[Calendly] RDV date extracted:', appointmentDate)
        } else {
          console.log('[Calendly] No start_time in payload, using current time')
        }

        // Supprimer TOUS les messages intermédiaires (Chargement, Choisissez un créneau)
        const allBotMessages = this.messagesContainer.querySelectorAll('.consullex-message.bot')
        const messagesToRemove: Element[] = []
        
        allBotMessages.forEach(msg => {
          const text = msg.textContent || ''
          // Supprimer les messages de chargement et de choix de créneau
          if (text.includes('Chargement') || text.includes('créneau') || text.includes('calendrier')) {
            messagesToRemove.push(msg)
          }
        })
        
        messagesToRemove.forEach(msg => msg.remove())
        console.log(`[Calendly] Removed ${messagesToRemove.length} intermediate messages`)

        // Supprimer le conteneur Calendly
        const container = document.getElementById('calendly-b2b')
        if (container) {
          container.remove()
          console.log('[Calendly] Calendly container removed')
        }

        // Créer le lead avec la vraie date du RDV
        await this.createLeadFromForm(formData, appointmentDate)
        
        // Enregistrer le RDV dans la DB
        try {
          await API_CLIENT.registerAppointment(this.sessionId!, {
            eventUri: event.uri || undefined,
            inviteeUri: invitee.uri || undefined,
            scheduledAt: scheduledAt
          })
          console.log('[Calendly] Appointment saved to DB with date:', scheduledAt)
        } catch (err) {
          console.error('[Calendly] Failed to save appointment:', err)
        }
      }
    }

    window.addEventListener('message', handleCalendlyEvent)

    // Timeout de sécurité après 5 minutes (si l'utilisateur ferme sans prendre RDV)
    setTimeout(() => {
      // Vérifier si le lead a déjà été créé
      if (this.currentFormData) {
        console.log('[Calendly] Timeout - showing fallback')
        window.removeEventListener('message', handleCalendlyEvent)
        this.showCalendlyFallbackButtons(formData)
      }
    }, 300000) // 5 minutes
  }

  /**
   * Affiche les boutons de secours si l'utilisateur ne prend pas RDV
   */
  private showCalendlyFallbackButtons(formData: FormData) {
    // Vérifier si le lead n'a pas déjà été créé
    if (!this.currentFormData) return

    // Vérifier si les boutons ne sont pas déjà affichés
    if (this.messagesContainer.querySelector('.consullex-calendly-fallback')) return

    this.addBotMessage("Avez-vous pu réserver votre créneau ?")

    const buttonsContainer = document.createElement('div')
    buttonsContainer.className = 'consullex-calendly-fallback'
    buttonsContainer.style.cssText = 'display: flex; gap: 8px; margin: 12px 0; flex-wrap: wrap;'

    const confirmBtn = document.createElement('button')
    confirmBtn.className = 'consullex-quick-reply'
    confirmBtn.textContent = "Oui, c'est fait"
    confirmBtn.onclick = async () => {
      buttonsContainer.remove()
      const container = document.getElementById('calendly-b2b')
      if (container) container.remove()
      await this.createLeadFromForm(formData, 'Confirmé via calendrier')
    }

    const skipBtn = document.createElement('button')
    skipBtn.className = 'consullex-quick-reply'
    skipBtn.textContent = "Non, je préfère être rappelé"
    skipBtn.onclick = async () => {
      buttonsContainer.remove()
      const container = document.getElementById('calendly-b2b')
      if (container) container.remove()
      this.conversationContext.wantsAppointment = false
      await this.createLeadFromForm(formData)
    }

    buttonsContainer.appendChild(confirmBtn)
    buttonsContainer.appendChild(skipBtn)
    this.messagesContainer.appendChild(buttonsContainer)
    this.scrollToBottom()
  }

  /**
   * Affiche Calendly pour prise de RDV (appelé par GPT via tool)
   * Utilise le SDK officiel Calendly
   */
  private async showCalendly(data: Record<string, unknown>) {
    const firstName = data.first_name as string || ''
    const lastName = data.last_name as string || ''
    const email = data.email as string || ''
    const phone = data.phone as string || ''

    try {
      // Charger le SDK Calendly
      await this.loadCalendlySDK()

      const { embedUrl } = await API_CLIENT.getCalendlyUrl(firstName, lastName, email)

      // Créer le conteneur Calendly
      const calendlyContainer = document.createElement('div')
      calendlyContainer.className = 'consullex-calendly-wrapper'
      calendlyContainer.id = 'calendly-gpt'
      calendlyContainer.style.cssText = 'min-height: 630px; background: white; margin: 12px 0; border-radius: 8px; overflow: hidden;'

      this.messagesContainer.appendChild(calendlyContainer)
      this.scrollToBottom()

      // Initialiser avec le SDK officiel
      const Calendly = (window as any).Calendly
      Calendly.initInlineWidget({
        url: embedUrl,
        parentElement: calendlyContainer,
        prefill: {
          name: `${firstName} ${lastName}`.trim(),
          email: email,
          customAnswers: {
            a1: phone
          }
        }
      })

      // Stocker les données pour le lead
      const formData: FormData = {
        firstName,
        lastName,
        email,
        phone,
        company: data.company as string || ''
      }

      // Écouter les événements Calendly
      this.setupCalendlyEventListener(formData)

    } catch (error) {
      console.error('Failed to load Calendly:', error)
      this.addBotMessage(
        "Désolé, impossible de charger le calendrier. Notre équipe vous recontactera directement."
      )
    }
  }

  /**
   * Crée un lead via l'API
   */
  private async createLead(data: Record<string, unknown>) {
    try {
      const result = await API_CLIENT.createLead(this.sessionId!, data)
      
      this.addBotMessage(
        `Votre demande a été enregistrée avec le numéro ${result.referenceNumber || 'CLX-XXXXXX'}.\n\n` +
        `Notre équipe vous recontactera sous 48h.\n\n` +
        `Merci pour votre confiance !`
      )
    } catch (error) {
      console.error('Failed to create lead:', error)
    }
  }

  /**
   * Termine la conversation
   */
  private endConversation(data: Record<string, unknown>) {
    const message = data.message as string || "Merci pour votre échange. À bientôt !"
    this.addBotMessage(message)
    this.setInputEnabled(false)
  }

  private addUserMessage(content: string) {
    const message: Message = {
      role: 'user',
      content,
      timestamp: new Date().toISOString(),
    }
    this.messages.push(message)
    this.renderMessage(message)
  }

  private addBotMessage(content: string, suggestedReplies?: string[]) {
    const message: Message = {
      role: 'assistant',
      content,
      timestamp: new Date().toISOString(),
    }
    this.messages.push(message)
    this.renderMessage(message, suggestedReplies)
  }

  private renderMessage(message: Message, suggestedReplies?: string[]) {
    const messageEl = document.createElement('div')
    messageEl.className = `consullex-message ${message.role === 'user' ? 'user' : 'bot'}`

    const avatar = document.createElement('div')
    avatar.className = 'consullex-message-avatar'
    if (message.role === 'user') {
      avatar.innerHTML = `<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>`
    } else {
      avatar.innerHTML = `<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M12 2L4 5v1h16V5L12 2zM4 8v1l3 8.5c-.5.5-.5 1.5 0 2s1.5.5 2 0l.5-.5h5l.5.5c.5.5 1.5.5 2 0s.5-1.5 0-2L20 9V8H4z"/></svg>`
    }

    const bubble = document.createElement('div')
    bubble.className = 'consullex-message-bubble'
    // Texte échappé : un message (utilisateur, IA ou formulaire) ne peut pas injecter de HTML dans la page
    bubble.innerHTML = escapeHtml(message.content).replace(/\n/g, '<br>')

    messageEl.appendChild(avatar)
    messageEl.appendChild(bubble)
    this.messagesContainer.appendChild(messageEl)

    // Quick replies
    if (suggestedReplies && suggestedReplies.length > 0) {
      const repliesContainer = document.createElement('div')
      repliesContainer.className = 'consullex-quick-replies'

      suggestedReplies.forEach((reply) => {
        const button = document.createElement('button')
        button.className = 'consullex-quick-reply'
        button.textContent = reply
        button.addEventListener('click', () => {
          repliesContainer.remove()
          this.sendMessage(reply)
        })
        repliesContainer.appendChild(button)
      })

      this.messagesContainer.appendChild(repliesContainer)
    }

    this.scrollToBottom()
  }

  private showTyping() {
    this.isTyping = true
    const typing = document.createElement('div')
    typing.className = 'consullex-message bot'
    typing.setAttribute('data-typing', 'true')
    typing.innerHTML = `
      <div class="consullex-message-avatar">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M12 2L4 5v1h16V5L12 2zM4 8v1l3 8.5c-.5.5-.5 1.5 0 2s1.5.5 2 0l.5-.5h5l.5.5c.5.5 1.5.5 2 0s.5-1.5 0-2L20 9V8H4z"/></svg>
      </div>
      <div class="consullex-message-bubble">
        <div class="consullex-typing">
          <div class="consullex-typing-dot"></div>
          <div class="consullex-typing-dot"></div>
          <div class="consullex-typing-dot"></div>
        </div>
      </div>
    `
    this.messagesContainer.appendChild(typing)
    this.scrollToBottom()
  }

  private hideTyping() {
    this.isTyping = false
    const typing = this.messagesContainer.querySelector('[data-typing]')
    if (typing) typing.remove()
  }

  private setInputEnabled(enabled: boolean) {
    this.inputField.disabled = !enabled
    this.sendButton.disabled = !enabled
    if (enabled) this.inputField.focus()
  }

  private scrollToBottom() {
    setTimeout(() => {
      this.messagesContainer.scrollTop = this.messagesContainer.scrollHeight
    }, 100)
  }

  public open() {
    this.isOpen = true
    this.window.classList.add('open')
    this.inputField.focus()
  }

  public close() {
    this.isOpen = false
    this.window.classList.remove('open')
  }

  public toggle() {
    this.isOpen ? this.close() : this.open()
  }

  public getElement(): HTMLElement {
    return this.container
  }

  public destroy() {
    this.container.remove()
  }
}
